/**
 * ASTT-7496 (QA #10/#20): CopilotChatFab.js wraps the whole launcher+panel
 * in a MUI ClickAwayListener so clicking anywhere outside closes the panel.
 * Exporting a conversation or a table triggers a download via a transient
 * <a> element appended to document.body (see csvExport.js) -- OUTSIDE the
 * wrapped subtree -- so that synthetic click registers as "clicked away"
 * and closes the panel mid-export.
 *
 * This is a tiny, framework-agnostic (no React) helper for "ignore the
 * next click-away because it was caused by that transient element, not a
 * genuine outside click" -- kept separate from CopilotChatFab.js so the
 * timing/expiry logic can be unit tested directly without mounting the
 * component (which would otherwise need react-oidc-context, axios, and
 * useCopilotAgentStatus all mocked just to exercise this one behavior).
 */
export function createClickAwaySuppressor(durationMs = 500) {
  let suppressed = false;
  let timer = null;

  return {
    /** Call right before triggering the transient DOM action (e.g. the
     * download anchor's synthetic click). */
    suppressNext() {
      suppressed = true;
      if (timer) clearTimeout(timer);
      // Safety net: if the anticipated click-away event never actually
      // fires (browser/timing variance -- e.g. some environments may not
      // bubble a click from an already-detached node), this expires on
      // its own rather than permanently swallowing the NEXT genuine
      // outside click.
      timer = setTimeout(() => {
        suppressed = false;
        timer = null;
      }, durationMs);
    },

    /** Call from the click-away handler itself. Returns true (and
     * consumes the suppression) if this click-away should be ignored. */
    consumeSuppression() {
      if (!suppressed) return false;
      suppressed = false;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      return true;
    },
  };
}
